import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Table,
  Alert,
  Tag,
  Tooltip,
  Skeleton,
  Empty,
  Input,
  Select,
  Segmented,
} from "antd";
import { format, differenceInCalendarDays } from "date-fns";
import {
  IconWallet,
  IconArrowUpRight,
  IconArrowDownRight,
  IconTrendingUp,
  IconReceipt2,
  IconCoins,
  IconInfoCircle,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { getUser } from "../../helpers/auth";
import {
  getFacilityCareFeeWallet,
  getFacilityCareFeeWalletLedger,
} from "../../helpers/api";

const fmt = (n) =>
  new Intl.NumberFormat("en-GH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const money = (n) => `GHS ${fmt(n)}`;

const ENTRY_LABELS = {
  care_utilization: "Care utilization",
  hq_manual_debit: "Manual debit",
  payout_debit: "Payout",
  hq_manual_credit: "Manual credit",
};

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const entryLabel = (type) => ENTRY_LABELS[type] || titleCase(type) || "Entry";

const isCredit = (type) => String(type || "").toLowerCase().includes("credit");

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Today" / "Yesterday" / "N days ago" for recent dates, else "MMM d, yyyy".
const relativeDay = (value) => {
  const d = parseDate(value);
  if (!d) return "—";
  const days = differenceInCalendarDays(new Date(), d);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return format(d, "MMM d, yyyy");
};

const clockTime = (value) => {
  const d = parseDate(value);
  return d ? format(d, "h:mm a") : "";
};

const fullDateTime = (value) => {
  const d = parseDate(value);
  return d ? format(d, "EEE, MMM d, yyyy · h:mm a") : "";
};

const DateCell = ({ value }) => {
  const d = parseDate(value);
  if (!d) return <span style={{ color: "#cbd5e1" }}>—</span>;
  return (
    <Tooltip title={fullDateTime(value)}>
      <div style={{ lineHeight: 1.25 }}>
        <div style={{ color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap" }}>
          {relativeDay(value)}
        </div>
        <div style={{ color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>
          {clockTime(value)}
        </div>
      </div>
    </Tooltip>
  );
};

const MiniMetric = ({ label, value, color }) => (
  <div
    style={{
      background: "#f8fafc",
      border: "1px solid #eef2f7",
      borderRadius: 12,
      padding: "10px 14px",
      height: "100%",
    }}
  >
    <div
      style={{
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontSize: 10,
        fontWeight: 600,
        color: "#94a3b8",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 16,
        fontWeight: 700,
        color: color || "#1e293b",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </div>
  </div>
);

const styles = {
  hero: {
    background: "linear-gradient(135deg, #0991cc 0%, #043c54 100%)",
    borderRadius: 20,
    padding: "28px 28px",
    color: "#ffffff",
    boxShadow: "0 12px 30px rgba(4, 60, 84, 0.28)",
    position: "relative",
    overflow: "hidden",
  },
  heroIcon: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: "rgba(255, 255, 255, 0.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  heroLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.82,
  },
  heroCurrency: {
    fontSize: 18,
    fontWeight: 600,
    opacity: 0.85,
    marginRight: 8,
  },
  heroValue: {
    fontSize: 40,
    fontWeight: 700,
    lineHeight: 1.05,
    fontVariantNumeric: "tabular-nums",
  },
  statCard: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 18,
    height: "100%",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    transition: "all 0.3s ease",
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: "#1e293b",
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
  },
  headerBadge: {
    width: 46,
    height: 46,
    borderRadius: 13,
    background: "linear-gradient(135deg, #0991cc 0%, #043c54 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 6px 14px rgba(9, 145, 204, 0.3)",
  },
  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    color: "#0991cc",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  tableWrap: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 8,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
};

const onStatEnter = (e) => {
  e.currentTarget.style.transform = "translateY(-3px)";
  e.currentTarget.style.boxShadow = "0 10px 22px rgba(4, 60, 84, 0.12)";
};
const onStatLeave = (e) => {
  e.currentTarget.style.transform = "translateY(0)";
  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.06)";
};

const StatCard = ({ label, value, icon, accent, hint }) => (
  <div
    style={styles.statCard}
    onMouseEnter={onStatEnter}
    onMouseLeave={onStatLeave}
  >
    <div className="d-flex align-items-start justify-content-between">
      <div className="pe-2" style={{ minWidth: 0 }}>
        <div style={styles.statLabel} className="d-flex align-items-center gap-1">
          {label}
          {hint ? (
            <Tooltip title={hint}>
              <IconInfoCircle size={13} color="#9ca3af" />
            </Tooltip>
          ) : null}
        </div>
        <div style={styles.statValue} className="mt-2">
          {value}
        </div>
      </div>
      <div
        style={{
          ...styles.statIcon,
          background: `${accent}14`,
          color: accent,
        }}
      >
        {icon}
      </div>
    </div>
  </div>
);

export const CareFeeWalletPage = () => {
  const user = getUser();
  const facilityId = user?.facility?.id;
  const role = user?.role;
  const allowed = role === "admin" || role === "superuser";

  const walletQ = useQuery(
    ["facility-care-fee-wallet", facilityId],
    () => getFacilityCareFeeWallet(facilityId),
    { enabled: !!facilityId && allowed }
  );

  const ledgerQ = useQuery(
    ["facility-care-fee-wallet-ledger", facilityId],
    () => getFacilityCareFeeWalletLedger(facilityId, { limit: 200, offset: 0 }),
    { enabled: !!facilityId && allowed }
  );

  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset to the first page whenever the active filters change.
  useEffect(() => {
    setPage(1);
  }, [search, direction, typeFilter]);

  const rows = useMemo(() => ledgerQ.data?.data || [], [ledgerQ.data]);

  const typeOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r?.entryType && !seen.has(r.entryType))
        seen.set(r.entryType, entryLabel(r.entryType));
    });
    return [
      { label: "All types", value: "all" },
      ...Array.from(seen, ([value, label]) => ({ label, value })),
    ];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      const credit = isCredit(r?.entryType);
      if (direction === "credit" && !credit) return false;
      if (direction === "debit" && credit) return false;
      if (typeFilter !== "all" && r?.entryType !== typeFilter) return false;
      if (!term) return true;
      const haystack = [entryLabel(r?.entryType), r?.notes, fmt(r?.amount)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search, direction, typeFilter]);

  const ledgerMetrics = useMemo(() => {
    let credited = 0;
    let debited = 0;
    filteredRows.forEach((r) => {
      const amt = Number(r?.amount) || 0;
      if (isCredit(r?.entryType)) credited += amt;
      else debited += amt;
    });
    return { count: filteredRows.length, credited, debited };
  }, [filteredRows]);

  if (!allowed) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Restricted"
        description="Only facility administrators can view the care fee wallet."
        className="mt-3"
      />
    );
  }

  if (!facilityId) {
    return (
      <Alert
        type="error"
        showIcon
        message="No active facility"
        description="We couldn't find an active facility on your account."
        className="mt-3"
      />
    );
  }

  const w = walletQ.data;

  const columns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (v) => <DateCell value={v} />,
    },
    {
      title: "Type",
      dataIndex: "entryType",
      key: "entryType",
      render: (v) => {
        const credit = isCredit(v);
        return (
          <Tag
            color={credit ? "green" : "red"}
            style={{
              borderRadius: 999,
              padding: "2px 10px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              margin: 0,
            }}
          >
            {credit ? (
              <IconArrowUpRight size={13} />
            ) : (
              <IconArrowDownRight size={13} />
            )}
            {entryLabel(v)}
          </Tag>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (v, row) => {
        const credit = isCredit(row?.entryType);
        const color = credit ? "#059669" : "#dc2626";
        return (
          <span
            style={{
              color,
              fontWeight: 700,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {credit ? "+ " : "− "}
            {money(v)}
          </span>
        );
      },
    },
    {
      title: "Balance after",
      dataIndex: "balanceAfter",
      key: "balanceAfter",
      align: "right",
      render: (v) => (
        <span
          style={{
            color: "#1e293b",
            fontWeight: 500,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {money(v)}
        </span>
      ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <span style={{ color: "#475569" }}>{v}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#cbd5e1" }}>—</span>
        ),
    },
  ];

  return (
    <section className="mt-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div className="d-flex align-items-center gap-3">
          <div style={styles.headerBadge}>
            <IconWallet size={24} color="#ffffff" />
          </div>
          <div>
            <h4 className="mb-1 anniversary-title" style={{ marginBottom: 2 }}>
              Care Fee Wallet
            </h4>
            <div className="text-muted small" style={{ maxWidth: 520 }}>
              Track care value earned and how it&apos;s debited across payouts
              and utilization.
            </div>
          </div>
        </div>
        <button
          type="button"
          style={styles.ghostBtn}
          onClick={() => {
            walletQ.refetch();
            ledgerQ.refetch();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f1f5f9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#ffffff";
          }}
        >
          <IconRefresh size={15} />
          Refresh
        </button>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12 col-lg-5">
          {walletQ.isLoading ? (
            <div style={{ ...styles.hero, background: "#e2e8f0" }}>
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>
          ) : (
            <div style={styles.hero}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div style={styles.heroIcon}>
                  <IconWallet size={26} color="#ffffff" />
                </div>
                <div style={styles.heroLabel}>Current balance</div>
              </div>
              <div className="d-flex align-items-baseline">
                <span style={styles.heroCurrency}>GHS</span>
                <span style={styles.heroValue}>{fmt(w?.balance)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="col-12 col-lg-7">
          <div className="row g-3 h-100">
            {walletQ.isLoading ? (
              [0, 1, 2].map((i) => (
                <div className="col-12 col-sm-6 col-lg-4" key={i}>
                  <div style={styles.statCard}>
                    <Skeleton active paragraph={{ rows: 1 }} title={false} />
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="col-12 col-sm-6 col-lg-4">
                  <StatCard
                    label="Care fee"
                    value={money(w?.careFee)}
                    accent="#0991cc"
                    icon={<IconReceipt2 size={20} />}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <StatCard
                    label="Multiplier"
                    value={`×${w?.multiplier ?? 1}`}
                    accent="#1d4f9a"
                    icon={<IconTrendingUp size={20} />}
                  />
                </div>
                <div className="col-12 col-sm-6 col-lg-4">
                  <StatCard
                    label="Expected care value"
                    value={money(w?.expectedCareValue)}
                    accent="#059669"
                    icon={<IconCoins size={20} />}
                    hint="Expected care value = care fee × multiplier"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {ledgerQ.isLoading ? (
        <div style={styles.tableWrap}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 pt-2 pb-3">
            <div>
              <h6 className="mb-0 fw-semibold" style={{ color: "#1e293b" }}>
                Debit &amp; credit log
              </h6>
              {(ledgerQ.data?.total ?? 0) > rows.length ? (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Showing latest {rows.length} of {ledgerQ.data.total} entries —
                  totals below reflect this view
                </div>
              ) : null}
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search type or notes"
                prefix={<IconSearch size={15} color="#94a3b8" />}
                style={{ width: 220, borderRadius: 10 }}
              />
              <Select
                value={typeFilter}
                onChange={setTypeFilter}
                options={typeOptions}
                style={{ minWidth: 150 }}
                dropdownMatchSelectWidth={false}
              />
              <Segmented
                value={direction}
                onChange={setDirection}
                options={[
                  { label: "All", value: "all" },
                  { label: "Credits", value: "credit" },
                  { label: "Debits", value: "debit" },
                ]}
              />
            </div>
          </div>

          <div className="row g-2 px-2 pb-3">
            <div className="col-4">
              <MiniMetric
                label="Entries"
                value={ledgerMetrics.count}
                color="#0991cc"
              />
            </div>
            <div className="col-4">
              <MiniMetric
                label="Credited"
                value={`+ ${money(ledgerMetrics.credited)}`}
                color="#059669"
              />
            </div>
            <div className="col-4">
              <MiniMetric
                label="Debited"
                value={`− ${money(ledgerMetrics.debited)}`}
                color="#dc2626"
              />
            </div>
          </div>

          <Table
            size="middle"
            rowKey="id"
            columns={columns}
            dataSource={filteredRows}
            pagination={{
              current: page,
              pageSize,
              total: filteredRows.length,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              size: "small",
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} entries`,
            }}
            scroll={{ x: "max-content" }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    rows.length
                      ? "No entries match your filters"
                      : "No ledger entries yet"
                  }
                />
              ),
            }}
          />
        </div>
      )}
    </section>
  );
};
